"use client";

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  createContext,
  useContext,
  ReactNode,
} from "react";
import { useSession } from "next-auth/react";
import {
  CREDITS_CHANGED_EVENT,
  CREDITS_CHANGED_REFETCH_DELAYS_MS,
} from "@/lib/credits-events";

// Import from centralized billing constants (fixes #4 - duplicate constants)
import {
  PLAN_ORDER as BILLING_PLAN_ORDER,
  PLAN_DISPLAY_NAMES as BILLING_PLAN_DISPLAY_NAMES,
  PLAN_LIMITS as BILLING_PLAN_LIMITS,
  PLAN_CREDITS as BILLING_PLAN_CREDITS,
  type PlanName,
  type PlanLimits as BillingPlanLimits,
} from "@/lib/constants/billing";

// ============================================
// TYPES
// ============================================

// Re-export PlanName from billing constants for backward compatibility
export type { PlanName };

// Note: custom branding and team collaboration are Enterprise (contact
// sales) offerings and are not part of the self-serve feature gates.
export type FeatureName =
  | "export_4k"
  | "export_1080p"
  | "export_720p"
  | "no_watermark"
  | "ai_assistant"
  | "ai_chat"
  | "ai_transcription"
  | "ai_clip_suggestions"
  | "priority_render"
  | "cloud_storage_100gb"
  | "unlimited_exports"
  | "long_video_support"
  | "distributed_render"
  | "api_access";

export interface PlanLimits {
  monthlyCredits: number;
  maxExportsPerMonth: number;
  maxResolution: "720p" | "1080p" | "4k";
  hasWatermark: boolean;
  storageGB: number;
  prioritySupport: boolean;
  aiFeatures: boolean;
  priorityRender: boolean;
  customBranding: boolean;
  teamCollaboration: boolean;
  maxVideoDurationMinutes: number;
  maxConcurrentRenders: number;
}

export interface SubscriptionData {
  plan: PlanName;
  hasSubscription: boolean;
  status: string;
  limits: PlanLimits;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  isLifetime?: boolean;
  billingCycle?: "monthly" | "annual";
  dodoCustomerId?: string;
}

/**
 * Backward-compatible alias used by components that previously imported
 * CreditBalanceData from credit-balance.tsx.  The canonical type is CreditData.
 */
export type CreditBalanceData = CreditData;

export interface CreditData {
  total: number;
  subscription: {
    remaining: number;
    used: number;
    total: number;
    periodEnd: string | null;
    plan: string;
    status: string;
  };
  payg: {
    balance: number;
    totalPurchased: number;
    totalUsed: number;
  };
  hasCredits: boolean;
  lowCredits: boolean;
  criticalCredits: boolean;
}

export interface SubscriptionContextValue {
  // Data
  subscription: SubscriptionData | null;
  credits: CreditData | null;

  // State
  loading: boolean;
  error: string | null;

  // Feature checks
  canUseFeature: (feature: FeatureName) => boolean;
  hasAtLeastPlan: (plan: PlanName) => boolean;
  getRequiredPlan: (feature: FeatureName) => PlanName;

  // Credit checks
  hasEnoughCredits: (amount: number) => boolean;

  // Utilities
  refetch: () => Promise<void>;
  getPlanDisplayName: (plan: PlanName) => string;
  getFeatureDescription: (feature: FeatureName) => string;
}

// ============================================
// CONSTANTS (from centralized billing module)
// ============================================

// Use centralized constants
const PLAN_ORDER = BILLING_PLAN_ORDER as PlanName[];
const PLAN_DISPLAY_NAMES = BILLING_PLAN_DISPLAY_NAMES;

const FEATURE_MIN_PLAN: Record<FeatureName, PlanName> = {
  export_720p: "free",
  export_1080p: "free",
  export_4k: "creator_2500",
  no_watermark: "creator_2500",
  ai_assistant: "creator_2500",
  ai_chat: "creator_2500",
  ai_transcription: "creator_2500",
  ai_clip_suggestions: "creator_2500",
  priority_render: "creator_5000",
  // 100GB is the Pro storage tier — Lite includes 25GB. Keep in sync with
  // FEATURE_MIN_PLAN in lib/subscription.ts.
  cloud_storage_100gb: "creator_5000",
  unlimited_exports: "creator_2500",
  long_video_support: "creator_5000",
  distributed_render: "creator_5000",
  api_access: "creator_5000",
};

const FEATURE_DESCRIPTIONS: Record<FeatureName, string> = {
  export_720p: "Export videos in 720p HD resolution",
  export_1080p: "Export videos in 1080p Full HD resolution",
  export_4k: "Export videos in 4K Ultra HD resolution",
  no_watermark: "Export videos without Loopdesk watermark",
  ai_assistant: "AI-powered editing assistant",
  ai_chat: "Chat with AI about your video content",
  ai_transcription: "Automatic video transcription",
  ai_clip_suggestions: "AI-suggested clip highlights",
  priority_render: "Priority render queue for faster exports",
  cloud_storage_100gb: "100GB cloud storage for your media",
  unlimited_exports: "Unlimited video exports per month",
  long_video_support: "Support for videos longer than 60 minutes",
  distributed_render: "Distributed rendering for faster processing",
  api_access: "API access for integrations",
};

const DEFAULT_PLAN_LIMITS: Record<PlanName, PlanLimits> = {
  free: {
    monthlyCredits: 0,
    maxExportsPerMonth: 3,
    maxResolution: "720p",
    hasWatermark: true,
    storageGB: 0, // No free plan — storage requires an active subscription
    prioritySupport: false,
    aiFeatures: false,
    priorityRender: false,
    customBranding: false,
    teamCollaboration: false,
    maxVideoDurationMinutes: 10,
    maxConcurrentRenders: 1,
  },
  creator_2500: {
    monthlyCredits: BILLING_PLAN_CREDITS.creator_2500,
    maxExportsPerMonth: -1,
    maxResolution: "4k",
    hasWatermark: false,
    storageGB: 25, // Creator Lite — matches the pricing page
    prioritySupport: false,
    aiFeatures: true,
    priorityRender: false,
    customBranding: false,
    teamCollaboration: false,
    maxVideoDurationMinutes: 60,
    maxConcurrentRenders: 2,
  },
  creator_5000: {
    monthlyCredits: BILLING_PLAN_CREDITS.creator_5000,
    maxExportsPerMonth: -1,
    maxResolution: "4k",
    hasWatermark: false,
    storageGB: 100,
    prioritySupport: true,
    aiFeatures: true,
    priorityRender: true,
    customBranding: false,
    teamCollaboration: false,
    maxVideoDurationMinutes: 120,
    maxConcurrentRenders: 3,
  },
};

// ============================================
// CONTEXT
// ============================================

const SubscriptionContext = createContext<SubscriptionContextValue | null>(
  null,
);

// ============================================
// PROVIDER
// ============================================

interface SubscriptionProviderProps {
  children: ReactNode;
  /** Auto-refresh interval in ms (0 to disable) */
  refreshInterval?: number;
}

export function SubscriptionProvider({
  children,
  refreshInterval = 0,
}: SubscriptionProviderProps) {
  const { data: session, status: sessionStatus } = useSession();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(
    null,
  );
  const [credits, setCredits] = useState<CreditData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // In-flight request deduplication — prevents concurrent fetches from
  // multiple effect triggers (e.g. sessionStatus changing from "loading"
  // to "authenticated" while session.user.id also becomes available).
  const inflightRef = useRef<Promise<void> | null>(null);

  const fetchData = useCallback(async () => {
    if (sessionStatus !== "authenticated" || !session?.user?.id) {
      setSubscription(null);
      setCredits(null);
      setLoading(false);
      return;
    }

    // If a fetch is already in-flight, piggyback on it
    if (inflightRef.current) {
      return inflightRef.current;
    }

    const doFetch = async () => {
      try {
        const response = await fetch("/api/credits");
        if (!response.ok) {
          throw new Error("Failed to fetch subscription data");
        }

        const data = await response.json();

        // Map API response to our data structures
        const plan = (data.balance?.subscription?.plan || "free") as PlanName;

        setSubscription({
          plan,
          hasSubscription: data.subscription?.hasSubscription ?? false,
          status: data.balance?.subscription?.status || "none",
          limits: DEFAULT_PLAN_LIMITS[plan] || DEFAULT_PLAN_LIMITS.free,
          currentPeriodEnd: data.balance?.subscription?.periodEnd,
          cancelAtPeriodEnd: data.subscription?.cancelAtPeriodEnd,
          isLifetime: data.subscription?.isLifetime ?? false,
          billingCycle: data.subscription?.billingCycle,
          dodoCustomerId: data.subscription?.dodoCustomerId,
        });

        setCredits({
          total: data.balance?.total ?? 0,
          subscription: {
            remaining: data.balance?.subscription?.remaining ?? 0,
            used: data.balance?.subscription?.used ?? 0,
            total: data.balance?.subscription?.total ?? 0,
            periodEnd: data.balance?.subscription?.periodEnd ?? null,
            plan: data.balance?.subscription?.plan ?? "free",
            status: data.balance?.subscription?.status ?? "none",
          },
          payg: {
            balance: data.balance?.payg?.balance ?? 0,
            totalPurchased: data.balance?.payg?.totalPurchased ?? 0,
            totalUsed: data.balance?.payg?.totalUsed ?? 0,
          },
          hasCredits: data.hasCredits ?? false,
          lowCredits: data.lowCredits ?? false,
          criticalCredits: data.criticalCredits ?? false,
        });

        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
        inflightRef.current = null;
      }
    };

    inflightRef.current = doFetch();
    return inflightRef.current;
  }, [session?.user?.id, sessionStatus]);

  // Initial fetch and session change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refetch on tab focus / visibility change instead of polling.
  // Credits only change as a result of discrete user actions (purchase,
  // render, AI usage, subscription change). Pages call refetch() explicitly
  // or signal via notifyCreditsChanged() (see the listener below). A
  // focus/visibility refresh covers the case where credits changed in
  // another tab or on the server while this tab was backgrounded, without
  // hammering /api/credits every minute on every open tab.
  useEffect(() => {
    if (sessionStatus !== "authenticated") return;

    const onFocus = () => {
      fetchData();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchData();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchData, sessionStatus]);

  // Refetch after in-app work that charges credits (see lib/credits-events).
  // Repeated signals restart the schedule so a burst costs one refetch pair.
  useEffect(() => {
    if (sessionStatus !== "authenticated") return;

    const timers: ReturnType<typeof setTimeout>[] = [];
    const clearTimers = () => {
      timers.splice(0).forEach((timer) => clearTimeout(timer));
    };
    const onCreditsChanged = () => {
      clearTimers();
      for (const delay of CREDITS_CHANGED_REFETCH_DELAYS_MS) {
        timers.push(setTimeout(() => void fetchData(), delay));
      }
    };

    window.addEventListener(CREDITS_CHANGED_EVENT, onCreditsChanged);
    return () => {
      window.removeEventListener(CREDITS_CHANGED_EVENT, onCreditsChanged);
      clearTimers();
    };
  }, [fetchData, sessionStatus]);

  // Optional polling, off by default. Keep the prop for backward-compat
  // and emergencies, but production callers should leave it at 0.
  useEffect(() => {
    if (refreshInterval > 0 && sessionStatus === "authenticated") {
      const interval = setInterval(fetchData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [fetchData, refreshInterval, sessionStatus]);

  // Feature check functions
  const canUseFeature = useCallback(
    (feature: FeatureName): boolean => {
      if (!subscription) return false;
      const requiredPlan = FEATURE_MIN_PLAN[feature];
      const userPlanIndex = PLAN_ORDER.indexOf(subscription.plan);
      const requiredPlanIndex = PLAN_ORDER.indexOf(requiredPlan);
      return userPlanIndex >= requiredPlanIndex;
    },
    [subscription],
  );

  const hasAtLeastPlan = useCallback(
    (plan: PlanName): boolean => {
      if (!subscription) return false;
      const userPlanIndex = PLAN_ORDER.indexOf(subscription.plan);
      const requiredPlanIndex = PLAN_ORDER.indexOf(plan);
      return userPlanIndex >= requiredPlanIndex;
    },
    [subscription],
  );

  const getRequiredPlan = useCallback((feature: FeatureName): PlanName => {
    return FEATURE_MIN_PLAN[feature];
  }, []);

  const hasEnoughCredits = useCallback(
    (amount: number): boolean => {
      if (!credits) return false;
      return credits.total >= amount;
    },
    [credits],
  );

  const getPlanDisplayName = useCallback((plan: PlanName): string => {
    return PLAN_DISPLAY_NAMES[plan] || plan;
  }, []);

  const getFeatureDescription = useCallback((feature: FeatureName): string => {
    return FEATURE_DESCRIPTIONS[feature] || feature;
  }, []);

  const value: SubscriptionContextValue = {
    subscription,
    credits,
    loading,
    error,
    canUseFeature,
    hasAtLeastPlan,
    getRequiredPlan,
    hasEnoughCredits,
    refetch: fetchData,
    getPlanDisplayName,
    getFeatureDescription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

// ============================================
// HOOKS
// ============================================

/**
 * Access the full subscription context
 */
export function useSubscription(): SubscriptionContextValue {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider",
    );
  }
  return context;
}

/**
 * Non-throwing variant of {@link useSubscription}.
 *
 * Returns `null` when called outside a `SubscriptionProvider` instead of
 * throwing. Use this in components that may be rendered in isolation (e.g.
 * the chat composer in unit tests, or any tree that doesn't guarantee the
 * provider) where subscription data is a progressive enhancement rather than
 * a hard dependency.
 */
export function useSubscriptionOptional(): SubscriptionContextValue | null {
  return useContext(SubscriptionContext);
}

/**
 * Check if a specific feature is available
 */
export function useFeatureAccess(feature: FeatureName): {
  hasAccess: boolean;
  loading: boolean;
  requiredPlan: PlanName;
  currentPlan: PlanName | null;
} {
  const { subscription, loading, canUseFeature, getRequiredPlan } =
    useSubscription();

  return {
    hasAccess: canUseFeature(feature),
    loading,
    requiredPlan: getRequiredPlan(feature),
    currentPlan: subscription?.plan ?? null,
  };
}

/**
 * Check if user has at least a specific plan
 */
export function usePlanAccess(minPlan: PlanName): {
  hasAccess: boolean;
  loading: boolean;
  currentPlan: PlanName | null;
} {
  const { subscription, loading, hasAtLeastPlan } = useSubscription();

  return {
    hasAccess: hasAtLeastPlan(minPlan),
    loading,
    currentPlan: subscription?.plan ?? null,
  };
}

/**
 * Check credit availability
 */
export function useCreditCheck(requiredAmount: number): {
  hasEnough: boolean;
  loading: boolean;
  availableCredits: number;
  deficit: number;
} {
  const { credits, loading, hasEnoughCredits } = useSubscription();

  const availableCredits = credits?.total ?? 0;
  const deficit = Math.max(0, requiredAmount - availableCredits);

  return {
    hasEnough: hasEnoughCredits(requiredAmount),
    loading,
    availableCredits,
    deficit,
  };
}

/**
 * Combined feature + credit check
 */
export function useAccessCheck(
  feature: FeatureName,
  requiredCredits: number,
): {
  hasFullAccess: boolean;
  hasFeatureAccess: boolean;
  hasEnoughCredits: boolean;
  loading: boolean;
  errors: string[];
  suggestions: string[];
} {
  const {
    loading,
    canUseFeature,
    hasEnoughCredits,
    getRequiredPlan,
    getPlanDisplayName,
    credits,
  } = useSubscription();

  const hasFeatureAccess = canUseFeature(feature);
  const hasCredits = hasEnoughCredits(requiredCredits);

  const errors: string[] = [];
  const suggestions: string[] = [];

  if (!hasFeatureAccess) {
    const requiredPlan = getRequiredPlan(feature);
    errors.push(
      `This feature requires ${getPlanDisplayName(requiredPlan)} plan`,
    );
    suggestions.push("Upgrade your plan to unlock this feature");
  }

  if (!hasCredits) {
    const available = credits?.total ?? 0;
    errors.push(
      `Insufficient credits (need ${requiredCredits}, have ${available})`,
    );
    suggestions.push("Purchase more credits to continue");
  }

  return {
    hasFullAccess: hasFeatureAccess && hasCredits,
    hasFeatureAccess,
    hasEnoughCredits: hasCredits,
    loading,
    errors,
    suggestions,
  };
}

/**
 * Get current plan limits
 */
export function usePlanLimits(): PlanLimits | null {
  const { subscription } = useSubscription();
  return subscription?.limits ?? null;
}

/**
 * Hook for render access check
 */
export function useRenderAccessCheck(options: {
  durationMinutes: number;
  resolution: "720p" | "1080p" | "4k";
  useDistributed?: boolean;
}): {
  canRender: boolean;
  loading: boolean;
  errors: string[];
  creditCost: number;
} {
  const {
    loading,
    canUseFeature,
    hasEnoughCredits,
    subscription,
    credits,
    getPlanDisplayName,
    getRequiredPlan,
  } = useSubscription();

  const { durationMinutes, resolution, useDistributed } = options;

  // Calculate credit cost
  let creditCost = Math.ceil(durationMinutes * 10); // Base: 10 credits per minute
  if (resolution === "4k") creditCost = Math.ceil(durationMinutes * 15);
  if (useDistributed) creditCost = Math.ceil(durationMinutes * 20);

  const errors: string[] = [];

  // Check resolution feature
  const resolutionFeature: FeatureName =
    resolution === "4k" ? "export_4k" : "export_1080p";
  if (!canUseFeature(resolutionFeature)) {
    const requiredPlan = getRequiredPlan(resolutionFeature);
    errors.push(
      `${resolution} export requires ${getPlanDisplayName(requiredPlan)} plan`,
    );
  }

  // Check distributed render
  if (useDistributed && !canUseFeature("distributed_render")) {
    const requiredPlan = getRequiredPlan("distributed_render");
    errors.push(
      `Distributed rendering requires ${getPlanDisplayName(requiredPlan)} plan`,
    );
  }

  // Check video duration
  const maxDuration = subscription?.limits?.maxVideoDurationMinutes ?? 10;
  if (maxDuration !== -1 && durationMinutes > maxDuration) {
    errors.push(`Your plan supports videos up to ${maxDuration} minutes`);
  }

  // Check credits
  if (!hasEnoughCredits(creditCost)) {
    const available = credits?.total ?? 0;
    errors.push(`Insufficient credits (need ${creditCost}, have ${available})`);
  }

  return {
    canRender: errors.length === 0,
    loading,
    errors,
    creditCost,
  };
}

/**
 * Drop-in replacement for the old standalone useCreditBalance hook.
 * Reads from the SubscriptionProvider context instead of making its own
 * fetch to /api/credits, eliminating duplicate network requests.
 */
export function useCreditBalance() {
  const { credits, loading, error, refetch } = useSubscription();

  return {
    balance: credits,
    loading,
    error,
    refetch,
    hasCredits: credits ? credits.total > 0 : false,
    isLow: credits ? credits.total < 100 : false,
    isCritical: credits ? credits.total < 20 : false,
  };
}

// ============================================
// UTILITY EXPORTS
// ============================================

export {
  PLAN_ORDER,
  PLAN_DISPLAY_NAMES,
  FEATURE_MIN_PLAN,
  FEATURE_DESCRIPTIONS,
  DEFAULT_PLAN_LIMITS,
};
